import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { defer, from, Observable, throwError } from 'rxjs';
import { catchError, switchMap, take, takeUntil } from 'rxjs/operators';
import { TosService } from '@services/tos.service';
import { AuthService } from '@services/auth.service';
import { environment } from '../../environments/environment';

@Injectable()
export class TosInterceptor implements HttpInterceptor {

  constructor(private tosService: TosService, private auth: AuthService) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const url = new URL(request.url, window.location.origin);
    const api = new URL(environment.apiBaseURL, window.location.origin);
    const apiPath = api.pathname.replace(/\/$/, '');
    if (url.origin !== api.origin ||
        (url.pathname !== apiPath && !url.pathname.startsWith(apiPath + '/'))) {
      return next.handle(request);
    }

    const sessionEnded$ = this.auth.shouldUseOidc() ? this.auth.oidc.cancel$ : this.auth.LogOutEvent;
    const isAcceptanceRequest = url.pathname.replace(/\/$/, '').toLowerCase().endsWith('/account/accept-tos');

    if (isAcceptanceRequest) return next.handle(request).pipe(takeUntil(sessionEnded$));

    return this.send(request, next).pipe(
      // Cover in-flight responses, suspended decisions and every recovery attempt.
      takeUntil(sessionEnded$)
    );
  }

  private send(request: HttpRequest<unknown>, next: HttpHandler, isRetry = false): Observable<HttpEvent<unknown>> {
    return defer(() => {
      const revision = this.tosService.acceptanceRevision;
      return next.handle(request).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status !== 403) {
            return throwError(() => error);
          }

          return from(this.isTosRequired(error)).pipe(
            switchMap(required => {
              if (!required) return throwError(() => error);

              // This response may have been decided before a now-completed acceptance.
              if (!isRetry && revision !== this.tosService.acceptanceRevision) {
                return this.send(request, next, true);
              }

              return this.tosService.waitForDecision(isRetry).pipe(
                take(1),
                switchMap(() => this.send(request, next, true))
              );
            })
          );
        })
      );
    });
  }

  private async isTosRequired(error: HttpErrorResponse): Promise<boolean> {
    if (error.statusText === 'TOS_REQUIRED') return true;

    try {
      // Download requests retain their binary response type even for API errors.
      const body = error.error instanceof Blob ? await error.error.text() :
        error.error instanceof ArrayBuffer ? new TextDecoder().decode(error.error) : error.error;
      return body === 'Terms of Service acceptance required.';
    } catch {
      // Preserve the original HTTP error if its body cannot be decoded.
      return false;
    }
  }
}
